import type { IStorageItem } from "@/core/entities/storage/StorageItem";
import type { ISerializationPort, IStoragePort } from "@/core/ports";
import { StorageError } from "@/infrastructure/errors/StorageError";

export interface KeyValueStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
  delete(key: string): void;
}

/** Shared retrieve/store/delete logic for local and session storage adapters. */
export class BrowserStorageAdapter implements IStoragePort {
  constructor(
    private readonly serializer: ISerializationPort,
    private readonly storageManager: KeyValueStorage,
  ) {}

  async retrieve<T>(key: string, mustCheckExpiration?: boolean): Promise<IStorageItem<T> | null> {
    try {
      const data = this.storageManager.get(key);
      if (!data) return null;
      const item = this.serializer.deserialize<IStorageItem<T>>(data);
      if (
        (item.expirationTimeStamp && item.expirationTimeStamp < Date.now()) ||
        (mustCheckExpiration && !item.expirationTimeStamp)
      ) {
        this.storageManager.delete(key);
        return null;
      }
      return item;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      throw new StorageError(`Failed to retrieve item: ${message}`, key);
    }
  }

  async store<T>(
    input: Pick<IStorageItem<T>, "key" | "value">,
    expirationDuration?: number,
  ): Promise<void> {
    const item: IStorageItem<T> = {
      ...input,
      timestamp: Date.now(),
    };
    if (expirationDuration) {
      item.expirationTimeStamp = item.timestamp + expirationDuration * 1000;
    }
    this.storageManager.set(item.key, this.serializer.serialize(item));
  }

  async delete(key: string): Promise<void> {
    this.storageManager.delete(key);
  }

  async clear(): Promise<void> {
    // Prefix-scoped clear not implemented
  }
}
